import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { SearchableTargetKind } from './findKinds';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { OutreachShell } from '../components/OutreachShell';
import { OutreachPlanCard } from '../components/OutreachPlanCard';
import { useTargetSearch, TargetFilters } from '../hooks/useTargetSearch';
import { useReleaseOptions } from '../hooks/useReleaseOptions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { outreachMessages } from '../outreachMessages';
import { FindTabs } from './FindTabs';
import { FindFilters } from './FindFilters';
import { FindSongPicker } from './FindSongPicker';
import { TargetTable } from './TargetTable';
import { FindEmpty } from './FindEmpty';
import { TargetDrawer } from './TargetDrawer';
import { findMessages as messages } from './findMessages';
import { S4aHelperModal } from './S4aHelperModal';
import { ProfileStateBar } from '../profile/ProfileStateBar';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FindScreenProps {
  accountId: number;
  initialSong: string | null;
  initialKind: SearchableTargetKind;
  /** The song whose Spotify for Artists helper opens at once (`?s4a=`), else null. */
  s4aSong?: string | null;
  onS4aClose?: () => void;
}

/**
 * Find (design outreach c1): the targets that fit the release, by kind, with the fit score, the
 * reasons, the contact icons and the flags. A row opens the drawer (c2). Outreach v2 has no pick:
 * Plug picks the people of a campaign every day. Free sees the top 10 of each kind and the plan card.
 */
const FindScreenComponent: React.FC<FindScreenProps> = ({
  accountId,
  initialSong,
  initialKind,
  s4aSong = null,
  onS4aClose,
}) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const { options } = useReleaseOptions(accountId);
  const s4aOption = useMemo(
    () => (s4aSong === null ? null : (options.find((option) => option.catalogItemUuid === s4aSong) ?? null)),
    [options, s4aSong]
  );
  const [filters, setFilters] = useState<TargetFilters>({
    kind: initialKind,
    catalogItemUuid: initialSong,
    q: '',
    country: null,
    minFollowers: null,
    maxFollowers: null,
    // On by default (owner goal 2026-10-20): the first list holds only people the artist can pitch.
    onlyWithEmail: true,
    includeFlagged: false,
  });
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  const search = useTargetSearch(accountId, filters);

  const song = useMemo(
    () => options.find((option) => option.catalogItemUuid === filters.catalogItemUuid) ?? null,
    [filters.catalogItemUuid, options]
  );
  const changeFilters = useCallback((patch: Partial<TargetFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  const content = () => {
    if (search.isLoading) {
      return <LoadingView />;
    }
    if (search.error !== null) {
      return <ErrorView error={search.error ?? undefined} retry={search.retry} />;
    }
    if (search.rows.length === 0) {
      return <FindEmpty accountId={accountId} />;
    }
    return (
      <>
        <TargetTable rows={search.rows} onOpen={setOpenUuid} />
        {search.hasMore && (
          <div>
            <SMLButton
              outline
              omitCap
              isLoading={search.isLoadingMore}
              onClick={() => search.loadMore().catch(console.error)}
            >
              {intl.formatMessage(outreachMessages.showMore)}
            </SMLButton>
          </div>
        )}
        {search.cappedAt !== null && (
          <p className={styles.small}>{intl.formatMessage(messages.cappedNote, { count: search.cappedAt })}</p>
        )}
      </>
    );
  };

  return (
    <OutreachShell
      section="tabs"
      tab={SMLRouteNames.outreachFind}
      pitchSong={filters.catalogItemUuid}
      heading={
        song === null
          ? intl.formatMessage(messages.titleNoSong)
          : intl.formatMessage(messages.title, { song: song.title })
      }
      lead={intl.formatMessage(messages.intro)}
      actions={
        <FindSongPicker
          options={options}
          value={filters.catalogItemUuid}
          onChange={(uuid) => changeFilters({ catalogItemUuid: uuid })}
        />
      }
    >
      {!plan.canPitch && <OutreachPlanCard subject={song?.title} />}
      <ProfileStateBar accountId={accountId} />
      <FindTabs kind={filters.kind} onChange={(kind) => changeFilters({ kind })} />
      <FindFilters filters={filters} onChange={changeFilters} shown={search.rows.length} total={search.total} />
      {content()}
      <TargetDrawer accountId={accountId} targetUuid={openUuid} song={song} onClose={() => setOpenUuid(null)} />
      {s4aOption !== null && <S4aHelperModal accountId={accountId} song={s4aOption} onClose={() => onS4aClose?.()} />}
    </OutreachShell>
  );
};

export const FindScreen = TypedMemo(FindScreenComponent);
